import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Save, Search, X, Plus, ChevronDown, Upload, Download, FileText, AlertTriangle } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import auditQuestionService from '../../../../../../services/audit_services/auditQuestionService';
import auditRiskService from '../../../../../../services/audit_services/auditRiskService';

export default function AuditQuestionEdit() {
  const { auditPlanId, sectionId, questionId } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [loading, setLoading] = useState({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [userRoleInPlan, setUserRoleInPlan] = useState(null); // User's role in the audit plan
  const [questionData, setQuestionData] = useState(null); // Store full question data

  // Risk search states
  const [riskOptions, setRiskOptions] = useState([]);
  const [riskSearch, setRiskSearch] = useState('');
  const [showRiskDropdown, setShowRiskDropdown] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [riskProbability, setRiskProbability] = useState('');
  const [riskConsequence, setRiskConsequence] = useState('');
  const [addingRisk, setAddingRisk] = useState(false);

  // Attachment states
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  // Form data organized by sections
  const [formData, setFormData] = useState({
    // Basic Information
    question_name: '',
    location: '',
    requirements: '',
    interview_plan: '',
    question_description: '',
    
    // Interview & Results
    answer: '',
    status: 'pending',
    declared_gaps: '',
    identified_best_practices: '',
    auditee_response_confirmation: 'pending',
    interview_result: '',
    
    // Related objects
    initial_observations: [],
    kpis: [],
    observations: [],
    observation_effectiveness: null,
    recommendations: [],
    current_question_risks: [], // Current AuditQuestionRisk records (for display and removal)
    new_auditplan_risks_to_add: [] // New AuditPlanRisk records to be added (from search)
  });

  // Track question risks to remove (AuditQuestionRisk IDs)
  const [auditQuestionRisksToRemove, setAuditQuestionRisksToRemove] = useState(new Set());

  const [errors, setErrors] = useState({});

  // Helper function to normalize probability values (same as audit plans)
  const normalizeProbabilityValue = (probability) => {
    if (!probability) return 'medium';
    
    const probabilityMap = {
      'Mineur': 'low',
      'mineur': 'low',
      'low': 'low',
      'Low': 'low',
      'Normal': 'medium',
      'normal': 'medium',
      'medium': 'medium',
      'Medium': 'medium',
      'Majeur': 'high',
      'majeur': 'high',
      'high': 'high',
      'High': 'high',
      'Élevé': 'high',
      'élevé': 'high',
      'Critique': 'critical',
      'critique': 'critical',
      'critical': 'critical',
      'Critical': 'critical'
    };
    
    return probabilityMap[probability] || 'medium';
  };

  // Search risks for dropdown
  const searchRisks = async (searchTerm) => {
    if (!auditPlanId) {
      console.log('Audit plan ID not available yet, auditPlanId:', auditPlanId);
      setRiskOptions([]);
      return;
    }
    
    console.log('Searching risks with term:', searchTerm, 'for audit plan:', auditPlanId);
    
    try {
      const result = await auditRiskService.searchAuditPlanRisks(authTokens.access, auditPlanId, searchTerm);
      console.log('Risk search result:', result);
      
      if (result.success) {
        setRiskOptions(result.data || []);
        console.log('Risk options set:', result.data);
      } else {
        console.error('Risk search failed:', result.error);
        setRiskOptions([]);
      }
    } catch (error) {
      console.error('Error searching audit plan risks:', error);
      setRiskOptions([]);
    }
  };

  useEffect(() => {
    fetchQuestionData();
  }, [auditPlanId, sectionId, questionId]);

  const fetchQuestionData = async () => {
    setInitialLoading(true);
    
    try {
      const result = await auditQuestionService.getAuditQuestionDetail(
        authTokens.access, 
        auditPlanId, 
        sectionId, 
        questionId
      );
      
      if (result.success) {
        const data = result.data;
        
        console.log('Question data received:', data);
        
        // Store the full question data
        setQuestionData(data);
        
        // Set user role from response
        if (data.my_role_in_audit_plan) {
          setUserRoleInPlan(data.my_role_in_audit_plan);
          // If user is not an auditor, redirect them back
          if (data.my_role_in_audit_plan !== 'auditor') {
            showError('Only auditors can edit questions for this audit section');
            navigate(`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}`);
            return;
          }
        }
        
        // Normalize probability values for current question risks
        const normalizedQuestionRisks = (data.question_risks || []).map(risk => ({
          ...risk,
          probability: normalizeProbabilityValue(risk.probability),
          consequence: risk.consequence || ''
        }));
        
        setFormData({
          question_name: data.question_name || '',
          location: data.location || '',
          requirements: data.requirements || '',
          interview_plan: data.interview_plan || '',
          question_description: data.question_description || '',
          answer: data.answer || '',
          status: data.status || 'pending',
          declared_gaps: data.declared_gaps || '',
          identified_best_practices: data.identified_best_practices || '',
          auditee_response_confirmation: data.auditee_response_confirmation || 'pending',
          interview_result: data.interview_result || '',
          initial_observations: data.initial_observations || [],
          kpis: data.kpis || [],
          observations: data.observations || [],
          observation_effectiveness: data.observation_effectiveness,
          recommendations: data.recommendations || [],
          current_question_risks: normalizedQuestionRisks,
          new_auditplan_risks_to_add: []
        });

        // Set attachments
        setAttachments(data.attachments || []);
      } else {
        showError(result.error || 'Erreur lors du chargement des données de la question d\'audit');
        navigate('/dashboard/audit/questions');
      }
    } catch (error) {
      console.error('Error fetching audit question data:', error);
      showError('Erreur lors du chargement des données de la question d\'audit');
      navigate('/dashboard/audit/questions');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleRiskSearch = (value) => {
    console.log('handleRiskSearch called with value:', value, 'auditPlanId:', auditPlanId);
    setRiskSearch(value);
    searchRisks(value);
  };

  const handleRiskSelect = (risk) => {
    setSelectedRisk(risk);
    setRiskSearch('');
    setShowRiskDropdown(false);
  };

  const handleRiskClear = () => {
    setSelectedRisk(null);
    setRiskSearch('');
    setRiskProbability('');
    setRiskConsequence('');
  };

  // Add audit plan risk to question (new system)
  const addAuditPlanRisk = (auditPlanRisk) => {
    console.log('➕ ADDING NEW AUDIT PLAN RISK (to question):', auditPlanRisk);
    
    const newRisk = {
      ...auditPlanRisk,
      probability: 'medium', // Default probability
      consequence: '' // Default empty consequence
    };
    
    // Add to new risks list
    setFormData(prev => ({
      ...prev,
      new_auditplan_risks_to_add: [...prev.new_auditplan_risks_to_add, newRisk]
    }));
    
    // Clear search
    setRiskSearch('');
    setRiskOptions([]);
    setShowRiskDropdown(false);
    setSelectedRisk(null);
    setRiskProbability('');
    setRiskConsequence('');
  };

  // Remove current question risk (add to removal list)
  const removeCurrentQuestionRisk = (questionRiskId) => {
    console.log('🗑️ REMOVING CURRENT QUESTION RISK:', questionRiskId);
    
    // Add to removal set
    setAuditQuestionRisksToRemove(prev => new Set([...prev, questionRiskId]));
    
    // Remove from current risks display
    setFormData(prev => ({
      ...prev,
      current_question_risks: prev.current_question_risks.filter(r => r.id !== questionRiskId)
    }));
  };

  // Remove new audit plan risk (remove from addition list)
  const removeNewAuditPlanRisk = (riskIndex) => {
    console.log('🗑️ REMOVING NEW AUDIT PLAN RISK at index:', riskIndex);
    
    setFormData(prev => ({
      ...prev,
      new_auditplan_risks_to_add: prev.new_auditplan_risks_to_add.filter((_, index) => index !== riskIndex)
    }));
  };

  // Update current question risk probability
  const updateCurrentQuestionRiskProbability = (riskId, probability) => {
    setFormData(prev => ({
      ...prev,
      current_question_risks: prev.current_question_risks.map(r => 
        r.id === riskId ? { ...r, probability } : r
      )
    }));
  };

  // Update current question risk consequence
  const updateCurrentQuestionRiskConsequence = (riskId, consequence) => {
    setFormData(prev => ({
      ...prev,
      current_question_risks: prev.current_question_risks.map(r => 
        r.id === riskId ? { ...r, consequence } : r
      )
    }));
  };

  // Update new audit plan risk probability
  const updateNewAuditPlanRiskProbability = (riskIndex, probability) => {
    setFormData(prev => ({
      ...prev,
      new_auditplan_risks_to_add: prev.new_auditplan_risks_to_add.map((r, index) => 
        index === riskIndex ? { ...r, probability } : r
      )
    }));
  };

  // Update new audit plan risk consequence
  const updateNewAuditPlanRiskConsequence = (riskIndex, consequence) => {
    setFormData(prev => ({
      ...prev,
      new_auditplan_risks_to_add: prev.new_auditplan_risks_to_add.map((r, index) => 
        index === riskIndex ? { ...r, consequence } : r
      )
    }));
  };

  // Attachment handlers
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingAttachment(true);
    try {
      // Create FormData using the service helper method
      const formData = auditQuestionService.createAttachmentFormData(file);
      const result = await auditQuestionService.uploadQuestionAttachment(authTokens.access, auditPlanId, sectionId, questionId, formData);
      if (result.success) {
        setAttachments(prev => [...prev, result.data]);
        showSuccess('Pièce jointe téléchargée avec succès');
      } else {
        showError(result.error || 'Erreur lors du téléchargement de la pièce jointe');
      }
    } catch (error) {
      console.error('Error uploading attachment:', error);
      showError('Erreur lors du téléchargement de la pièce jointe');
    } finally {
      setUploadingAttachment(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      const result = await auditQuestionService.deleteQuestionAttachment(authTokens.access, auditPlanId, sectionId, questionId, attachmentId);
      if (result.success) {
        setAttachments(prev => prev.filter(att => att.id !== attachmentId));
        showSuccess('Pièce jointe supprimée avec succès');
      } else {
        showError(result.error || 'Erreur lors de la suppression de la pièce jointe');
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      showError('Erreur lors de la suppression de la pièce jointe');
    }
  };

  // Handle dynamic arrays
  const addInitialObservation = () => {
    setFormData(prev => ({
      ...prev,
      initial_observations: [...prev.initial_observations, { observation: '' }]
    }));
  };

  const removeInitialObservation = (index) => {
    setFormData(prev => ({
      ...prev,
      initial_observations: prev.initial_observations.filter((_, i) => i !== index)
    }));
  };

  const updateInitialObservation = (index, value) => {
    setFormData(prev => ({
      ...prev,
      initial_observations: prev.initial_observations.map((obs, i) => 
        i === index ? { ...obs, observation: value } : obs
      )
    }));
  };

  const addKPI = () => {
    setFormData(prev => ({
      ...prev,
      kpis: [...prev.kpis, { kpi_name: '', kpi_value: '', kpi_target: '', kpi_description: '' }]
    }));
  };

  const removeKPI = (index) => {
    setFormData(prev => ({
      ...prev,
      kpis: prev.kpis.filter((_, i) => i !== index)
    }));
  };

  const updateKPI = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      kpis: prev.kpis.map((kpi, i) => 
        i === index ? { ...kpi, [field]: value } : kpi
      )
    }));
  };

  const addObservation = () => {
    setFormData(prev => ({
      ...prev,
      observations: [...prev.observations, { observation: '' }]
    }));
  };

  const removeObservation = (index) => {
    setFormData(prev => ({
      ...prev,
      observations: prev.observations.filter((_, i) => i !== index)
    }));
  };

  const updateObservation = (index, value) => {
    setFormData(prev => ({
      ...prev,
      observations: prev.observations.map((obs, i) => 
        i === index ? { ...obs, observation: value } : obs
      )
    }));
  };

  const addRecommendation = () => {
    setFormData(prev => ({
      ...prev,
      recommendations: [...prev.recommendations, { recommendation: '', priority: 'medium' }]
    }));
  };

  const removeRecommendation = (index) => {
    setFormData(prev => ({
      ...prev,
      recommendations: prev.recommendations.filter((_, i) => i !== index)
    }));
  };

  const updateRecommendation = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      recommendations: prev.recommendations.map((rec, i) => 
        i === index ? { ...rec, [field]: value } : rec
      )
    }));
  };

  const saveSection = async (sectionName, sectionData) => {
    setLoading(prev => ({ ...prev, [sectionName]: true }));

    try {
      const result = await auditQuestionService.patchAuditQuestion(authTokens.access, auditPlanId, sectionId, questionId, sectionData);

      if (result.success) {
        showSuccess(`${sectionName} section saved successfully`);
        // Update form data with response to get any server-side changes
        if (result.data) {
          // Update relevant fields from response
          Object.keys(sectionData).forEach(key => {
            if (result.data[key] !== undefined) {
              setFormData(prev => ({ ...prev, [key]: result.data[key] }));
            }
          });
        }
      } else {
        showError(result.error || `Error saving ${sectionName} section`);
        if (result.errorData) {
          setErrors(result.errorData);
        }
      }
    } catch (error) {
      console.error(`Error saving ${sectionName} section:`, error);
      showError(`Error saving ${sectionName} section`);
    } finally {
      setLoading(prev => ({ ...prev, [sectionName]: false }));
    }
  };

  const saveBasicInfo = () => {
    // Validate required fields
    const newErrors = {};
    
    if (!formData.question_name.trim()) {
      newErrors.question_name = 'Question name is required';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (!formData.question_description.trim()) {
      newErrors.question_description = 'Question description is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showError('Please fill in all required fields');
      return;
    }
    
    const sectionData = {
      question_name: formData.question_name,
      location: formData.location,
      requirements: formData.requirements,
      interview_plan: formData.interview_plan,
      question_description: formData.question_description
    };
    saveSection('Basic Information', sectionData);
  };

  const saveInterviewResults = () => {
    const sectionData = {
      answer: formData.answer,
      status: formData.status,
      declared_gaps: formData.declared_gaps,
      identified_best_practices: formData.identified_best_practices,
      interview_result: formData.interview_result
    };
    saveSection('Interview & Results', sectionData);
  };

  const saveInitialObservations = () => {
    const sectionData = {
      initial_observations_data: formData.initial_observations.filter(obs => obs.observation && obs.observation.trim())
    };
    saveSection('Initial Observations', sectionData);
  };

  const saveKPIs = () => {
    const sectionData = {
      kpis_data: formData.kpis.filter(kpi => kpi.kpi_name && kpi.kpi_name.trim())
    };
    saveSection('KPIs', sectionData);
  };

  const saveObservations = () => {
    const sectionData = {
      observations_data: formData.observations.filter(obs => obs.observation && obs.observation.trim()),
      observation_effectiveness: formData.observation_effectiveness
    };
    saveSection('Observations', sectionData);
  };

  const saveRecommendations = () => {
    const sectionData = {
      recommendations_data: formData.recommendations.filter(rec => rec.recommendation && rec.recommendation.trim())
    };
    saveSection('Recommendations', sectionData);
  };

  const saveRisks = () => {
    // Prepare the complete question data with risk updates
    const questionData = {
      // Include all current form data
      ...formData,
      // Add the new risk management fields
      auditquestion_risks_to_remove: Array.from(auditQuestionRisksToRemove),
      auditplan_risks_to_add: (formData.new_auditplan_risks_to_add || []).map(r => r.id),
      current_question_risks_updates: (formData.current_question_risks || []).map(r => ({
        id: r.id,
        probability: r.probability,
        consequence: r.consequence
      }))
    };
    
    // Use the updated service method
    saveRisksSection(questionData);
  };

  const saveRisksSection = async (questionData) => {
    setLoading(prev => ({ ...prev, 'Risks': true }));

    try {
      const result = await auditQuestionService.updateAuditQuestion(
        authTokens.access, 
        auditPlanId, 
        sectionId, 
        questionId, 
        questionData,
        auditQuestionRisksToRemove
      );

      if (result.success) {
        showSuccess('Risks section saved successfully');
        
        // Clear the removal set and update form data with response
        setAuditQuestionRisksToRemove(new Set());
        
        if (result.data && result.data.question_risks) {
          // Update form data with the response to reflect server state
          const normalizedQuestionRisks = result.data.question_risks.map(risk => ({
            ...risk,
            probability: normalizeProbabilityValue(risk.probability),
            consequence: risk.consequence || ''
          }));
          
          setFormData(prev => ({
            ...prev,
            current_question_risks: normalizedQuestionRisks,
            new_auditplan_risks_to_add: []
          }));
        }
      } else {
        showError(result.error || 'Failed to save risks section');
      }
    } catch (error) {
      console.error('Error saving risks section:', error);
      showError('Error saving risks section');
    } finally {
      setLoading(prev => ({ ...prev, 'Risks': false }));
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données de la question...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Modifier la question d'audit</h1>
            <p className="text-sm text-gray-600 mt-1">Mettre à jour les détails de la question par sections</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Informations de base</h2>
              <button
                onClick={saveBasicInfo}
                disabled={loading['Basic Information']}
                className="inline-flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4 mr-1" />
                {loading['Basic Information'] ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de la question <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.question_name}
                  onChange={(e) => handleInputChange('question_name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.question_name ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.question_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.question_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Localisation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.location ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.location && (
                  <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Exigences</label>
                <textarea
                  value={formData.requirements}
                  onChange={(e) => handleInputChange('requirements', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Plan d'entretien</label>
                <textarea
                  value={formData.interview_plan}
                  onChange={(e) => handleInputChange('interview_plan', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description de la question <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.question_description}
                  onChange={(e) => handleInputChange('question_description', e.target.value)}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.question_description ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.question_description && (
                  <p className="mt-1 text-sm text-red-600">{errors.question_description}</p>
                )}
              </div>
            </div>
          </div>

          {/* Interview & Results Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Entretien et résultats</h2>
              <button
                onClick={saveInterviewResults}
                disabled={loading['Interview & Results']}
                className="inline-flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4 mr-1" />
                {loading['Interview & Results'] ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Réponse</label>
                <textarea
                  value={formData.answer}
                  onChange={(e) => handleInputChange('answer', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Entrez la réponse de l'entretien..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="pending">En attente</option>
                    <option value="in_progress">En cours</option>
                    <option value="completed">Terminé</option>
                    <option value="on_hold">En pause</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Résultat de l'entretien</label>
                  <input
                    type="text"
                    value={formData.interview_result}
                    onChange={(e) => handleInputChange('interview_result', e.target.value)}
                    placeholder="Entrez le résultat de l'entretien..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Écarts déclarés</label>
                <textarea
                  value={formData.declared_gaps}
                  onChange={(e) => handleInputChange('declared_gaps', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Décrivez les écarts identifiés..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meilleures pratiques identifiées</label>
                <textarea
                  value={formData.identified_best_practices}
                  onChange={(e) => handleInputChange('identified_best_practices', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Décrivez les meilleures pratiques identifiées..."
                />
              </div>
            </div>
          </div>

          {/* Risk Assessment Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Évaluation des risques</h2>
              <button
                onClick={saveRisks}
                disabled={loading['Risks']}
                className="inline-flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4 mr-1" />
                {loading['Risks'] ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Current Question Risks (AuditQuestionRisk) */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Risques actuels de cette question</h3>
                {formData.current_question_risks.length === 0 ? (
                  <p className="text-gray-500 text-sm">Aucun risque associé à cette question.</p>
                ) : (
                  <div className="space-y-3">
                    {formData.current_question_risks.map((questionRisk) => (
                      <div key={`current-${questionRisk.id}`} className="border border-green-200 rounded-lg p-4 bg-green-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <AlertTriangle className="h-4 w-4 text-green-600" />
                              <h4 className="font-medium text-green-900">{questionRisk.risk_name || questionRisk.name}</h4>
                            </div>
                            {questionRisk.description && (
                              <p className="text-sm text-green-700 mb-3">{questionRisk.description}</p>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Probabilité</label>
                                <select
                                  value={questionRisk.probability}
                                  onChange={(e) => updateCurrentQuestionRiskProbability(questionRisk.id, e.target.value)}
                                  className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                >
                                  <option value="low">Mineur</option>
                                  <option value="medium">Normal</option>
                                  <option value="high">Majeur</option>
                                  <option value="critical">Critique</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Conséquence</label>
                                <input
                                  type="text"
                                  value={questionRisk.consequence}
                                  onChange={(e) => updateCurrentQuestionRiskConsequence(questionRisk.id, e.target.value)}
                                  placeholder="Entrer la conséquence..."
                                  className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent w-full"
                                />
                              </div>
                            </div>
                            {questionRisk.actions && questionRisk.actions.length > 0 && (
                              <div className="mt-2">
                                <span className="font-medium text-green-800 text-sm">Actions: </span>
                                <span className="text-green-700 text-sm">{questionRisk.actions.length} action(s)</span>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => removeCurrentQuestionRisk(questionRisk.id)}
                            className="text-red-600 hover:text-red-800 ml-2"
                            title="Supprimer ce risque"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* New Audit Plan Risks to Add */}
              {formData.new_auditplan_risks_to_add.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Nouveaux risques à ajouter</h3>
                  <div className="space-y-3">
                    {formData.new_auditplan_risks_to_add.map((auditPlanRisk, index) => (
                      <div key={`new-${index}`} className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <AlertTriangle className="h-4 w-4 text-blue-600" />
                              <h4 className="font-medium text-blue-900">{auditPlanRisk.risk_name || auditPlanRisk.name}</h4>
                            </div>
                            {auditPlanRisk.description && (
                              <p className="text-sm text-blue-700 mb-3">{auditPlanRisk.description}</p>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Probabilité</label>
                                <select
                                  value={auditPlanRisk.probability}
                                  onChange={(e) => updateNewAuditPlanRiskProbability(index, e.target.value)}
                                  className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                  <option value="low">Mineur</option>
                                  <option value="medium">Normal</option>
                                  <option value="high">Majeur</option>
                                  <option value="critical">Critique</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Conséquence</label>
                                <input
                                  type="text"
                                  value={auditPlanRisk.consequence}
                                  onChange={(e) => updateNewAuditPlanRiskConsequence(index, e.target.value)}
                                  placeholder="Entrer la conséquence..."
                                  className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                                />
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => removeNewAuditPlanRisk(index)}
                            className="text-red-600 hover:text-red-800 ml-2"
                            title="Supprimer ce risque"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add New Risk from Audit Plan */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Ajouter un risque du plan d'audit</h3>
                
                <div className="relative">
                  <div className="flex items-center">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher un risque du plan d'audit..."
                      value={riskSearch}
                      onChange={(e) => {
                        setRiskSearch(e.target.value);
                        if (e.target.value.length >= 2) {
                          searchRisks(e.target.value);
                          setShowRiskDropdown(true);
                        } else {
                          setShowRiskDropdown(false);
                        }
                      }}
                      onFocus={() => {
                        if (riskSearch.length >= 2) {
                          setShowRiskDropdown(true);
                        }
                      }}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => setShowRiskDropdown(!showRiskDropdown)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </button>
                  </div>
                  
                  {showRiskDropdown && riskOptions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {riskOptions.map((risk) => (
                        <button
                          key={risk.id}
                          onClick={() => {
                            addAuditPlanRisk(risk);
                            setShowRiskDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{risk.risk_name || risk.name}</div>
                          {risk.description && (
                            <div className="text-sm text-gray-500 truncate">{risk.description}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Initial Observations Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Observations initiales</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={addInitialObservation}
                  className="inline-flex items-center px-3 py-1 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter
                </button>
                <button
                  onClick={saveInitialObservations}
                  disabled={loading['Initial Observations']}
                  className="inline-flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-1" />
                  {loading['Initial Observations'] ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
            
            <div className="space-y-3">
              {formData.initial_observations.length === 0 ? (
                <p className="text-gray-500 text-sm">Aucune observation initiale ajoutée.</p>
              ) : (
                formData.initial_observations.map((observation, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-1">
                      <textarea
                        value={observation.observation}
                        onChange={(e) => updateInitialObservation(index, e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder={`Observation initiale ${index + 1}...`}
                      />
                    </div>
                    <button
                      onClick={() => removeInitialObservation(index)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* KPIs Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">KPIs</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={addKPI}
                  className="inline-flex items-center px-3 py-1 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter
                </button>
                <button
                  onClick={saveKPIs}
                  disabled={loading['KPIs']}
                  className="inline-flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-1" />
                  {loading['KPIs'] ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              {formData.kpis.length === 0 ? (
                <p className="text-gray-500 text-sm">Aucun KPI ajouté.</p>
              ) : (
                formData.kpis.map((kpi, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-700">KPI {index + 1}</h3>
                      <button
                        onClick={() => removeKPI(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Nom du KPI</label>
                        <input
                          type="text"
                          value={kpi.kpi_name}
                          onChange={(e) => updateKPI(index, 'kpi_name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Valeur actuelle</label>
                        <input
                          type="text"
                          value={kpi.kpi_value}
                          onChange={(e) => updateKPI(index, 'kpi_value', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Valeur cible</label>
                        <input
                          type="text"
                          value={kpi.kpi_target}
                          onChange={(e) => updateKPI(index, 'kpi_target', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                        <input
                          type="text"
                          value={kpi.kpi_description}
                          onChange={(e) => updateKPI(index, 'kpi_description', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Observations Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Observations</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={addObservation}
                  className="inline-flex items-center px-3 py-1 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter
                </button>
                <button
                  onClick={saveObservations}
                  disabled={loading['Observations']}
                  className="inline-flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-1" />
                  {loading['Observations'] ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
            
            <div className="space-y-4">

              {/* Observations List */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Observations</label>
                <div className="space-y-3">
                  {formData.observations.length === 0 ? (
                    <p className="text-gray-500 text-sm">Aucune observation ajoutée.</p>
                  ) : (
                    formData.observations.map((observation, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="flex-1">
                          <textarea
                            value={observation.observation}
                            onChange={(e) => updateObservation(index, e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            placeholder={`Observation ${index + 1}...`}
                          />
                        </div>
                        <button
                          onClick={() => removeObservation(index)}
                          className="p-2 text-red-600 hover:text-red-800"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>


              {/* Observation Effectiveness */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Efficacité de l'observation</label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="observation_effectiveness"
                      value="true"
                      checked={formData.observation_effectiveness === true}
                      onChange={() => handleInputChange('observation_effectiveness', true)}
                      className="mr-2"
                    />
                    Oui
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="observation_effectiveness"
                      value="false"
                      checked={formData.observation_effectiveness === false}
                      onChange={() => handleInputChange('observation_effectiveness', false)}
                      className="mr-2"
                    />
                    Non
                  </label>
                </div>
              </div>

            </div>
          </div>

          {/* Recommendations Section - Only show if observation effectiveness is No */}
          {formData.observation_effectiveness === false && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Recommandations</h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={addRecommendation}
                    className="inline-flex items-center px-3 py-1 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter
                  </button>
                  <button
                    onClick={saveRecommendations}
                    disabled={loading['Recommendations']}
                    className="inline-flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {loading['Recommendations'] ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              </div>
              
              <div className="space-y-4">
                {formData.recommendations.length === 0 ? (
                  <p className="text-gray-500 text-sm">Aucune recommandation ajoutée.</p>
                ) : (
                  formData.recommendations.map((recommendation, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-gray-700">Recommandation {index + 1}</h3>
                        <button
                          onClick={() => removeRecommendation(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Recommandation</label>
                          <textarea
                            value={recommendation.recommendation}
                            onChange={(e) => updateRecommendation(index, 'recommendation', e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            placeholder="Entrez la recommandation..."
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Priorité</label>
                          <select
                            value={recommendation.priority}
                            onChange={(e) => updateRecommendation(index, 'priority', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          >
                            <option value="low">Faible</option>
                            <option value="medium">Moyen</option>
                            <option value="high">Élevé</option>
                            <option value="critical">Critique</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Guide de modification</h3>
            <div className="space-y-3 text-sm text-gray-600">
              <p>• Chaque section peut être enregistrée indépendamment</p>
              <p>• Les modifications sont enregistrées immédiatement lorsque vous cliquez sur Enregistrer</p>
              <p>• Les éléments vides dans les tableaux seront automatiquement supprimés</p>
              <p>• Vous pouvez continuer à modifier d'autres sections après en avoir enregistré une</p>
            </div>
          </div>

          {/* Attachments Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Pièces jointes</h2>
              <div className="flex items-center space-x-2">
                <input
                  type="file"
                  id="attachment-upload"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={uploadingAttachment}
                />
                <label
                  htmlFor="attachment-upload"
                  className={`inline-flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer ${
                    uploadingAttachment ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  {uploadingAttachment ? 'Téléchargement...' : 'Télécharger un fichier'}
                </label>
              </div>
            </div>
            
            <div className="space-y-3">
              {attachments.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">Aucune pièce jointe téléchargée.</p>
              ) : (
                attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{attachment.file_name}</p>
                        <p className="text-xs text-gray-500">
                          {attachment.file_size && `${attachment.file_size} • `}
                          Téléchargé le {new Date(attachment.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => window.open(attachment.file_url, '_blank')}
                        className="inline-flex items-center px-2 py-1 text-xs text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50 transition-colors"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Télécharger
                      </button>
                      <button
                        onClick={() => handleDeleteAttachment(attachment.id)}
                        className="p-1 text-red-600 hover:text-red-800"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmation de l'audité</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Statut actuel:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  formData.auditee_response_confirmation === 'confirmed' ? 'bg-green-100 text-green-800' :
                  formData.auditee_response_confirmation === 'rejected' ? 'bg-red-100 text-red-800' :
                  formData.auditee_response_confirmation === 'needs_clarification' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {formData.auditee_response_confirmation === 'pending' ? 'En attente' :
                   formData.auditee_response_confirmation === 'confirmed' ? 'Confirmé' :
                   formData.auditee_response_confirmation === 'rejected' ? 'Rejeté' :
                   formData.auditee_response_confirmation === 'needs_clarification' ? 'Nécessite une clarification' :
                   'Inconnu'}
                </span>
              </div>
              <p className="text-xs text-gray-500">Seuls les audités peuvent modifier le statut de confirmation</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}